import type { Locale, SectorDefinition, Theme } from '../types';
import { chrome, formatDataDate } from '../i18n';
import { PlatformBar } from '../components/PlatformBar';
import { Breadcrumb } from '../components/Breadcrumb';
import { RegionTable } from '../components/RegionTable';
import { AlertsPanel } from '../components/AlertsPanel';
import { icon } from '../components/Icon';
import { domainScore, domainRag } from '../core/score';
import type { SegmentSelection } from '../core/segments';
import { FilterControls } from '../components/FilterControls';
import { attainment } from '../core/targets';

interface Props {
  sectors: SectorDefinition[];
  active: SectorDefinition;
  locale: Locale;
  theme: Theme;
  selection: SegmentSelection;
  hasCommitments: boolean;
}

/** Tier 2 — sector overview: sub-domain cards + regional status + owned alerts. */
export function SectorView({ sectors, active, locale, theme, selection, hasCommitments }: Props): string {
  const c = chrome(locale);
  const content = active.content[locale];

  const domainCards = content.domains
    .map((d) => {
      const score = domainScore(active, d, locale);
      const rag = domainRag(active, d, locale);
      const kpis = content.kpis.filter((k) => d.kpis.includes(k.id)).slice(0, 2);
      const minis = kpis
        .map((k) => `<div class="mini-kpi"><span>${k.label}</span><b>${k.value} <small>${k.unit}</small></b></div>`)
        .join('');
      const offCount = content.kpis
        .filter((k) => d.kpis.includes(k.id))
        .filter((k) => {
          const a = attainment(k);
          return a !== undefined && a < 90;
        }).length;
      return `
      <a class="card domain-card ${rag ?? ''}" style="--accent:${active.accent}" href="#/${active.id}/${d.id}" aria-label="${d.name}">
        <div class="label">
          <span class="ic" style="background:${active.accent}22;color:${active.accent}">${icon(d.icon, 'icon icon-kpi')}</span>
          <b>${d.name}</b>
        </div>
        <div class="domain-sub">${d.subtitle}</div>
        ${score !== undefined ? `<div class="score-row"><span class="val">${score}<small>%</small></span><span class="unit">${c.compositeLabel}</span></div>` : ''}
        <div class="mini-kpis">${minis}</div>
        <div class="card-foot">
          <span class="${offCount ? 'warn' : ''}">${icon('target', 'icon icon-xs')} ${offCount} ${c.targetsAtRisk}</span>
          <span class="open">${c.viewDomain} ${locale === 'ar' ? '←' : '→'}</span>
        </div>
      </a>`;
    })
    .join('');

  return `
    ${PlatformBar(sectors, active.id, locale, theme, hasCommitments, formatDataDate(locale, active.updatedAt))}
    ${Breadcrumb([
      { label: c.navNational, href: 'national', icon: 'gauge' },
      { label: content.navLabel, icon: active.icon },
    ])}
    <section class="toolbar">
      ${FilterControls(content, locale, selection)}
    </section>
    <div class="section-title">${c.subdomains}</div>
    <section class="grid overview-cards">${domainCards}</section>
    <section class="grid cols" style="margin-top:16px">
      ${RegionTable({
        title: content.regions.title,
        subtitle: content.regions.subtitle,
        headers: content.regions.headers,
        rows: content.regions.rows,
        locale,
        hasRecs: Boolean(content.regions.recommendations?.length),
        recHint: c.recHint,
      })}
      ${AlertsPanel(content.alerts.title, content.alerts.subtitle, content.alerts.items, locale, Boolean(content.alerts.recommendations?.length), c.recHint)}
    </section>
    <div id="modal-root"></div>`;
}

